import React, { useState, useEffect } from "react";
import ProfileCompletionScreen from "./ProfileCompletionScreen";
import SearchBuyersScreen from "./SearchBuyersScreen";
import BuyerOfferScreen from "./BuyerOfferScreen";
import ContactPermissionScreen from "./ContactPermissionScreen";
import RewardConfirmationScreen from "./RewardConfirmationScreen";
import PostCompletionScreen from "./PostCompletionScreen";
import HandoffScreen from "./HandoffScreen";
import OptOutScreen from "./OptOutScreen";
import BuyersWillBeInTouchScreen from "./BuyersWillBeInTouchScreen";
import RewardsAlreadyGivenScreen from "./RewardsAlreadyGivenScreen";
import ProfileSummaryScreen from "./ProfileSummaryScreen";
import AADirectCallScreen from "./AADirectCallScreen";

// Define all possible stages in the success flow
const SUCCESS_STAGES = {
  PROFILE_SUMMARY: "profile_summary",
  PROFILE_COMPLETE: "profile_complete",
  SEARCH: "search",
  OFFER: "offer",
  AADIRECT_CALL: "aadirect_call",
  CONTACT_PERMISSION: "contact_permission",
  REWARD_CONFIRMATION: "reward_confirmation",
  POST_COMPLETION: "post_completion",
  HANDOFF: "handoff",
  OPT_OUT: "opt_out",
  BUYERS_WILL_BE_IN_TOUCH: "buyers_will_be_in_touch", // New stage
};

const Success = ({ userData, onComplete, onFullApp }) => {
  // Track the current stage
  const [stage, setStage] = useState(SUCCESS_STAGES.PROFILE_SUMMARY);

  // Store data from previous stages
  const [buyerData, setBuyerData] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [rewardAmount, setRewardAmount] = useState(null);
  const [qualifiedUser, setQualifiedUser] = useState(null);

  // Determine if the user is qualified based on their profile and survey data
  useEffect(() => {
    // Check if userData or session storage indicates whether the user is qualified
    // This should be based on the criteria mentioned in the product document

    const surveyData = JSON.parse(sessionStorage.getItem("surveyData") || "{}");
    const postAuthSurveyData = JSON.parse(
      sessionStorage.getItem("postAuthSurveyCompleteData") || "{}"
    );

    // Example qualification logic - modify based on actual criteria
    // For example, check if they have auto insurance and are interested in exploring options
    const hasCurrentInsurance = surveyData["SQUE_Wv7dh88Cd9"]?.value === "Yes"; // Check if they have current insurance
    const interestedInExploring =
      surveyData["SQUE_Z44cEsJ99D"]?.value === "Yes"; // Check if they're interested in exploring
    const hasCompletedPostSurvey = Object.keys(postAuthSurveyData).length > 0;

    // Set qualified status
    //setQualifiedUser(hasCurrentInsurance && interestedInExploring && hasCompletedPostSurvey);
    setQualifiedUser(true);
  }, [userData]);

  const handleSummaryComplete = () => {
    setStage(SUCCESS_STAGES.PROFILE_COMPLETE);
  };

  const handleBackToInsuranceOptions = () => {
    // Call the parent's onComplete with a special flag to indicate navigation back
    // if (onComplete) {
    //   onComplete({ action: "navigate_back", step: "insurance-options" });
    // }
    window.history.back();
  };
  const handleEditProfile = () => {
    // This could redirect back to the survey or contact info screens
    // For now, we'll just log it - you can implement the actual navigation
    console.log("Edit profile requested");
    // Example: navigate back to a specific step in the main app flow
    // onComplete({ action: 'edit', step: 'contact-info' });
  };
  // Handlers for stage transitions
  const handleProfileComplete = () => {
    setStage(SUCCESS_STAGES.SEARCH);
    // if(!buyerData){
    //   setStage(SUCCESS_STAGES.BUYERS_WILL_BE_IN_TOUCH);
    // } else {
    //   setStage(SUCCESS_STAGES.SEARCH);
    // }
  };

  const handleBuyersFound = (buyer) => {
    if (buyer) {
      setBuyerData(buyer);

      // Check if this is an existing reward
      if (buyer.hasExistingReward) {
        // Get the buyer name to determine the flow
        const buyerName = buyer.name?.toLowerCase() || "";
        // SimplyIOA rewards require contact permission
        if (buyerName === "simplyioa") {
          setStage(SUCCESS_STAGES.CONTACT_PERMISSION);
        } else {
          // All other rewards go to the reward already given screen
          setStage(SUCCESS_STAGES.REWARD_CONFIRMATION);
        }
      } else {
        // Regular offer flow
        setStage(SUCCESS_STAGES.OFFER);
      }
    } else if (qualifiedUser === false) {
      // No buyer found and user is not qualified
      setStage(SUCCESS_STAGES.BUYERS_WILL_BE_IN_TOUCH);
    } else {
      // Handle case where search returned no buyers but user is qualified
      // For now, direct them to the BUYERS_WILL_BE_IN_TOUCH stage
      setStage(SUCCESS_STAGES.BUYERS_WILL_BE_IN_TOUCH);
    }
  };

  const handleAcceptOffer = (buyer) => {
    const buyerName = buyer.name?.toLowerCase() || "";
    setBuyerData(buyer);
    // heck if this is AADirect
    if (buyerName === "aadirect" || buyer.isAADirect) {
      setStage(SUCCESS_STAGES.AADIRECT_CALL);
    } else if (buyerName === "simplyioa") {
      //SimplyIOA rewards require contact permission
      setStage(SUCCESS_STAGES.CONTACT_PERMISSION);
    } else {
      // All other rewards go to the reward already given screen
      // setStage(SUCCESS_STAGES.REWARD_ALREADY_GIVEN);
      setStage(SUCCESS_STAGES.REWARD_CONFIRMATION);
    }
  };

  const handleRejectOffer = () => {
    setStage(SUCCESS_STAGES.OPT_OUT);
  };

  const handleContactSubmit = (data) => {
    setContactData(data);
    setStage(SUCCESS_STAGES.REWARD_CONFIRMATION);

    // Set the reward amount (would typically come from the API)
    setRewardAmount(buyerData?.offer?.amount || 5.0);
  };

  const handleRewardConfirmed = () => {
    setStage(SUCCESS_STAGES.POST_COMPLETION);
  };

  const handleViewWallet = () => {
    window.open("https://www.ownli.app/rewards", "_blank");
  };

  const handleDownloadApp = () => {
    // In a real app, this would either:
    // 1. Open the app store link
    // 2. Show a QR code to download
    // For now, just open a mock URL
    window.open("https://www.ownli.co/individuals", "_blank");
  };

  const handleBackToContactPermission = () => {
    setStage(SUCCESS_STAGES.CONTACT_PERMISSION);
  };

  const handleBackToOffer = () => {
    setStage(SUCCESS_STAGES.OFFER);
  };

  const handleInsuranceProfile = () => {
    setStage(SUCCESS_STAGES.SEARCH);
  };

  // Render the appropriate component based on the current stage
  const renderCurrentStage = () => {
    switch (stage) {
      case SUCCESS_STAGES.PROFILE_SUMMARY:
        return (
          <ProfileSummaryScreen
            userData={userData}
            onContinue={handleSummaryComplete}
            onEdit={handleEditProfile}
            onBack={handleBackToInsuranceOptions}
          />
        );

      case SUCCESS_STAGES.PROFILE_COMPLETE:
        return (
          <ProfileCompletionScreen
            userData={userData}
            onContinue={handleProfileComplete}
          />
        );

      case SUCCESS_STAGES.SEARCH:
        return (
          <SearchBuyersScreen
            userData={userData}
            onBuyersFound={handleBuyersFound}
            isQualifiedUser={qualifiedUser}
          />
        );
      case SUCCESS_STAGES.REWARD_ALREADY_GIVEN:
        return (
          <RewardsAlreadyGivenScreen
            buyerData={buyerData}
            onViewWallet={handleViewWallet}
            onDownloadApp={handleDownloadApp}
          />
        );
      case SUCCESS_STAGES.OFFER:
        return (
          <BuyerOfferScreen
            buyerData={buyerData}
            onAcceptOffer={handleAcceptOffer}
            onReject={handleRejectOffer}
          />
        );
      case SUCCESS_STAGES.AADIRECT_CALL:
        return (
          <AADirectCallScreen
            buyerData={buyerData}
            onComplete={handleViewWallet}
          />
        );
      case SUCCESS_STAGES.CONTACT_PERMISSION:
        return (
          <ContactPermissionScreen
            buyerData={buyerData}
            userData={userData}
            onComplete={handleContactSubmit}
            onBack={handleBackToOffer}
          />
        );

      case SUCCESS_STAGES.REWARD_CONFIRMATION:
        return (
          <RewardConfirmationScreen
            buyerData={buyerData}
            contactData={contactData}
            onContinue={handleViewWallet}
          />
        );

      case SUCCESS_STAGES.POST_COMPLETION:
        return (
          <PostCompletionScreen
            buyerData={buyerData}
            rewardAmount={rewardAmount}
            onViewWallet={handleViewWallet}
            onDownloadApp={handleDownloadApp}
          />
        );

      case SUCCESS_STAGES.HANDOFF:
        return <HandoffScreen userData={userData} buyerData={buyerData} />;

      case SUCCESS_STAGES.BUYERS_WILL_BE_IN_TOUCH:
        return (
          <BuyersWillBeInTouchScreen
            userData={userData}
            onDownloadApp={handleDownloadApp}
            onFullApp={onFullApp}
            onInsuranceProfile={handleInsuranceProfile}
          />
        );

      case SUCCESS_STAGES.OPT_OUT:
        return (
          <OptOutScreen
            userData={userData}
            buyerData={buyerData}
            onFullApp={onFullApp}
            onDownloadApp={handleDownloadApp}
          />
        );

      default:
        return (
          <ProfileSummaryScreen
            userData={userData}
            onContinue={handleSummaryComplete}
            onEdit={handleEditProfile}
          />
        );
    }
  };

  return <div className="p-4">{renderCurrentStage()}</div>;
};

export default Success;
